import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { SearchResults } from '../types/search';
import { useTheme } from '../components/ThemeContext';
import { RiskBadge } from '../components';
import ProductIntelBlock, { getField, useMediaQuery } from '../components/ProductIntelBlock';

interface SearchPageProps {
  searchInputRef: React.RefObject<HTMLInputElement | null>;
  searchQuery: string;
  setSearchQuery: (value: string) => void;
  handleSearch: (queryOverride?: string) => void;
  isSearching: boolean;
  searchResults: SearchResults | null;
  copyToClipboard: (text: string) => Promise<void>;
  SectionHeader: React.ComponentType<{ children: React.ReactNode; sub?: string }>;
  IconSearch: React.ComponentType;
  IconSpinner: React.ComponentType;
  IconBot: React.ComponentType;
  IconStar: React.ComponentType;
  onNavigateToAccount?: () => void;
  onNavigateToProduct?: (productId: number) => void;
}

// ============================================================
// SEARCH HISTORY TYPES & HELPERS
// ============================================================
interface SearchHistoryEntry {
  id: string;
  query: string;
  timestamp: number;
  resultCount: number;
}

const HISTORY_STORAGE_KEY = 'iremedy-search-history';
const MAX_HISTORY_ENTRIES = 20;

function loadHistory(): SearchHistoryEntry[] {
  try {
    const raw = localStorage.getItem(HISTORY_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed;
  } catch {
    return [];
  }
}

function saveHistory(entries: SearchHistoryEntry[]): void {
  try {
    localStorage.setItem(HISTORY_STORAGE_KEY, JSON.stringify(entries));
  } catch {
    // localStorage full or unavailable — silently fail
  }
}

function formatRelativeTime(timestamp: number): string {
  const now = Date.now();
  const diff = now - timestamp;
  const seconds = Math.floor(diff / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(timestamp).toLocaleDateString();
}

// ============================================================
// HELPER: Detect if results look like product data
// ============================================================
function isProductResult(row: Record<string, unknown>): boolean {
  const keys = Object.keys(row).map(k => k.toLowerCase());
  return keys.includes('product_name') || keys.includes('ndc_code');
}

// getField / useMediaQuery now live in components/ProductIntelBlock.tsx
// (imported above) — single definition shared with the reusable block.

// ============================================================
// COMPONENT: Product Briefing Card (with expandable profile)
// ============================================================
function BriefingCard({
  row,
  index,
  expanded,
  onToggleExpand,
  onNavigate,
}: {
  row: Record<string, unknown>;
  index: number;
  expanded: boolean;
  onToggleExpand: () => void;
  onNavigate?: (id: number) => void;
}) {
  const { T } = useTheme();
  const [hovered, setHovered] = useState(false);

  const productName = getField(row, 'product_name', 'proprietary_name', 'name') as string | null;
  const genericName = getField(row, 'generic_name') as string | null;
  const ndc = getField(row, 'ndc_code', 'ndc') as string | null;
  const riskLevel = getField(row, 'supply_chain_risk', 'risk', 'risk_level') as string | null;
  const taaEligible = getField(row, 'taa_eligible') as boolean | null;
  const country = getField(row, 'country_of_origin', 'api_source_country', 'finished_goods_country') as string | null;
  const manufacturer = getField(row, 'manufacturer', 'manufacturer_name') as string | null;
  const dosageForm = getField(row, 'dosage_form') as string | null;
  const strength = getField(row, 'strength') as string | null;
  const shortageStatus = getField(row, 'shortage_status') as string | null;

  return (
    <div
      onClick={onToggleExpand}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: T.white,
        border: `1px solid ${expanded ? T.gold : hovered ? T.gold : T.border}`,
        borderRadius: T.radius,
        padding: 0,
        boxShadow: expanded ? T.shadowMd : hovered ? T.shadowMd : T.shadow,
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        overflow: 'hidden',
      }}
    >
      {/* Gold top accent when expanded */}
      {expanded && (
        <div style={{
          height: 3,
          background: `linear-gradient(90deg, ${T.gold}, ${T.gold}80, transparent)`,
        }} />
      )}

      {/* Card Header */}
      <div style={{
        padding: '16px 20px 12px',
        borderBottom: `1px solid ${T.borderLight}`,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: 16,
        flexWrap: 'wrap',
      }}>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <span style={{
              fontSize: 10, fontWeight: 700, color: T.gold,
              textTransform: 'uppercase', letterSpacing: '0.1em',
              background: `${T.gold}15`, padding: '2px 8px', borderRadius: 4,
            }}>
              #{index + 1}
            </span>
            {ndc && (
              <span style={{
                fontSize: 11, fontWeight: 600, color: T.textLight,
                fontFamily: "'IBM Plex Mono', monospace",
              }}>
                NDC {ndc}
              </span>
            )}
          </div>
          <div style={{
            fontSize: 16, fontWeight: 700, color: T.navy,
            lineHeight: 1.3, marginBottom: 4,
          }}>
            {productName || 'Unknown Product'}
          </div>
          {genericName && genericName !== productName && (
            <div style={{ fontSize: 13, color: T.textMid, marginBottom: 2 }}>
              {genericName}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 6 }}>
            {manufacturer && (
              <span style={{
                fontSize: 11, color: T.textMid, display: 'inline-flex',
                alignItems: 'center', gap: 4,
              }}>
                <span style={{ color: T.textLight }}>Mfr:</span> {manufacturer}
              </span>
            )}
            {dosageForm && (
              <span style={{ fontSize: 11, color: T.textMid }}>
                {dosageForm}{strength ? ` ${strength}` : ''}
              </span>
            )}
          </div>
        </div>

        {/* Expand indicator (confidence ring removed: products.confidence_score
            is the 0.80 column default on every row — a constant conveys nothing) */}
        <div style={{
          display: 'flex', gap: 12, alignItems: 'flex-start', flexShrink: 0,
        }}>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            width: 28, height: 28, borderRadius: '50%',
            background: expanded ? `${T.gold}15` : T.offWhite,
            border: `1px solid ${expanded ? T.gold : T.border}`,
            alignSelf: 'center',
            transition: 'all 0.2s',
            flexShrink: 0,
          }}>
            <span style={{
              fontSize: 12, color: expanded ? T.gold : T.textLight,
              transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s',
              display: 'inline-block', lineHeight: 1,
            }}>
              {"\u25BC"}
            </span>
          </div>
        </div>
      </div>

      {/* Card Body - Compliance & Details (collapsed summary) */}
      {!expanded && (
        <div style={{
          padding: '12px 20px 16px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 12,
          flexWrap: 'wrap',
        }}>
          {/* Badges Row */}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            {riskLevel && <RiskBadge risk={riskLevel} />}
            {taaEligible != null && (
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                background: taaEligible ? '#ECFDF5' : '#FEF2F2',
                color: taaEligible ? '#059669' : '#DC2626',
                border: `1px solid ${taaEligible ? '#BBF7D0' : '#FECACA'}`,
                borderRadius: 20, padding: '4px 14px', fontSize: 11, fontWeight: 700,
              }}>
                TAA {taaEligible ? 'Eligible' : 'N/A'}
              </span>
            )}
            {shortageStatus && shortageStatus.toLowerCase() !== 'none' && (
              <span style={{
                background: T.warningBg, color: T.warning,
                border: '1px solid #FDE68A', borderRadius: 20,
                padding: '4px 14px', fontSize: 11, fontWeight: 700,
                textTransform: 'uppercase', letterSpacing: '0.04em',
              }}>
                Shortage: {shortageStatus}
              </span>
            )}
          </div>

          {/* Origin Info */}
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexShrink: 0 }}>
            {country && (
              <span style={{
                fontSize: 12, fontWeight: 600, color: T.textMid,
                display: 'inline-flex', alignItems: 'center', gap: 4,
                background: T.offWhite, padding: '4px 12px', borderRadius: 20,
                border: `1px solid ${T.border}`,
              }}>
                {'\uD83C\uDF0D'} {country}
              </span>
            )}
            <span style={{
              fontSize: 11, fontWeight: 600, color: T.gold,
              display: 'inline-flex', alignItems: 'center', gap: 4,
            }}>
              Expand Profile {'\u25BC'}
            </span>
          </div>
        </div>
      )}

      {/* Expanded: reusable product intelligence block (pathway + compliance + facts) */}
      {expanded && (
        <ProductIntelBlock
          row={row}
          onNavigate={onNavigate}
        />
      )}
    </div>
  );
}

// ============================================================
// COMPONENT: Fallback table for non-product results
// ============================================================
function ResultsTable({
  results,
  resultCount,
}: {
  results: Record<string, unknown>[];
  resultCount?: number;
}) {
  const { T } = useTheme();

  return (
    <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: T.radius, overflow: 'hidden', boxShadow: T.shadow }}>
      <div style={{ padding: '16px 20px', borderBottom: `1px solid ${T.border}`, background: T.offWhite }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: T.textLight, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
          Query Results ({resultCount ?? results.length} rows)
        </span>
      </div>
      <div style={{ overflowX: 'auto', maxHeight: 400 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: T.offWhite }}>
              {Object.keys(results[0]).map(key => (
                <th key={key} style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, color: T.textDark, borderBottom: `1px solid ${T.border}`, whiteSpace: 'nowrap' }}>
                  {key}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {results.slice(0, 20).map((row, i) => (
              <tr key={i} style={{ borderBottom: `1px solid ${T.borderLight}` }}>
                {Object.values(row).map((val: unknown, j) => (
                  <td key={j} style={{ padding: '10px 16px', color: T.textMid, maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {val === null ? <span style={{ color: T.textLight, fontStyle: 'italic' }}>null</span> : String(val)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {results.length > 20 && (
          <div style={{ padding: '12px 20px', textAlign: 'center', color: T.textLight, fontSize: 12, background: T.offWhite }}>
            Showing 20 of {results.length} results
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================
// COMPONENT: Search History Panel
// ============================================================
function SearchHistoryPanel({
  history,
  onRerun,
  onRemove,
  onClear,
}: {
  history: SearchHistoryEntry[];
  onRerun: (query: string) => void;
  onRemove: (id: string) => void;
  onClear: () => void;
}) {
  const { T } = useTheme();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [expanded, setExpanded] = useState(false);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [confirmClear, setConfirmClear] = useState(false);

  if (history.length === 0) return null;

  const handleClear = () => {
    if (confirmClear) {
      onClear();
      setConfirmClear(false);
    } else {
      setConfirmClear(true);
      setTimeout(() => setConfirmClear(false), 3000);
    }
  };

  const visibleEntries = expanded ? history : history.slice(0, 5);

  return (
    <div style={{
      background: T.white,
      border: `1px solid ${T.border}`,
      borderRadius: T.radius,
      overflow: 'hidden',
      boxShadow: T.shadow,
    }}>
      {/* Header */}
      <div
        onClick={() => setExpanded(!expanded)}
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: isMobile ? '12px 16px' : '14px 20px',
          background: T.offWhite,
          borderBottom: `1px solid ${T.border}`,
          cursor: 'pointer',
          userSelect: 'none',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={T.gold} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <polyline points="12 6 12 12 16 14" />
          </svg>
          <span style={{
            fontSize: 11,
            fontWeight: 700,
            color: T.textLight,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
          }}>
            Recent Searches
          </span>
          <span style={{
            fontSize: 10,
            fontWeight: 700,
            color: T.gold,
            background: `${T.gold}15`,
            padding: '2px 8px',
            borderRadius: 10,
          }}>
            {history.length}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button
            onClick={(e) => { e.stopPropagation(); handleClear(); }}
            style={{
              padding: '4px 12px',
              borderRadius: 6,
              border: `1px solid ${confirmClear ? T.danger : T.border}`,
              background: confirmClear ? T.dangerBg : 'transparent',
              color: confirmClear ? T.danger : T.textLight,
              fontSize: 11,
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.2s',
              fontFamily: 'inherit',
            }}
          >
            {confirmClear ? 'Confirm Clear' : 'Clear All'}
          </button>
          <span style={{
            color: T.textLight,
            fontSize: 12,
            transition: 'transform 0.2s',
            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
            display: 'inline-flex',
          }}>
            {'\u25BC'}
          </span>
        </div>
      </div>

      {/* History Entries */}
      <div style={{ maxHeight: expanded ? 400 : 260, overflowY: 'auto' }}>
        {visibleEntries.map((entry) => {
          const isHovered = hoveredId === entry.id;
          return (
            <div
              key={entry.id}
              onMouseEnter={() => setHoveredId(entry.id)}
              onMouseLeave={() => setHoveredId(null)}
              onClick={() => onRerun(entry.query)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
                padding: isMobile ? '10px 16px' : '12px 20px',
                borderBottom: `1px solid ${T.borderLight}`,
                cursor: 'pointer',
                background: isHovered ? `${T.gold}08` : 'transparent',
                transition: 'background 0.15s',
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: isHovered ? T.navy : T.textDark,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  transition: 'color 0.15s',
                }}>
                  {entry.query}
                </div>
                <div style={{
                  display: 'flex',
                  gap: 12,
                  marginTop: 4,
                  fontSize: 11,
                  color: T.textLight,
                }}>
                  <span>{formatRelativeTime(entry.timestamp)}</span>
                  <span style={{ color: T.border }}>{'\u2022'}</span>
                  <span>{entry.resultCount} {entry.resultCount === 1 ? 'result' : 'results'}</span>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                {isHovered && (
                  <button
                    onClick={(e) => { e.stopPropagation(); onRemove(entry.id); }}
                    title="Remove from history"
                    style={{
                      width: 24,
                      height: 24,
                      borderRadius: 4,
                      border: 'none',
                      background: T.dangerBg,
                      color: T.danger,
                      fontSize: 14,
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontFamily: 'inherit',
                      padding: 0,
                    }}
                  >
                    {'\u00D7'}
                  </button>
                )}
                <span style={{
                  fontSize: 14,
                  color: isHovered ? T.gold : T.textLight,
                  transition: 'color 0.15s',
                }}>
                  {'\u21BB'}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Show more / less toggle */}
      {history.length > 5 && (
        <button
          onClick={() => setExpanded(!expanded)}
          style={{
            width: '100%',
            padding: '10px 20px',
            border: 'none',
            borderTop: `1px solid ${T.borderLight}`,
            background: T.offWhite,
            color: T.textMid,
            fontSize: 12,
            fontWeight: 600,
            cursor: 'pointer',
            fontFamily: 'inherit',
            textAlign: 'center',
          }}
        >
          {expanded ? `Show Less \u25B2` : `Show All ${history.length} Searches \u25BC`}
        </button>
      )}
    </div>
  );
}

// ============================================================
// MAIN: Search Page
// ============================================================
export default function SearchPage({
  searchInputRef,
  searchQuery,
  setSearchQuery,
  handleSearch,
  isSearching,
  searchResults,
  copyToClipboard,
  SectionHeader,
  IconSearch,
  IconSpinner,
  IconBot,
  IconStar,
  onNavigateToAccount,
  onNavigateToProduct,
}: SearchPageProps) {
  const { T } = useTheme();
  const [showSql, setShowSql] = useState(false);
  const [expandedCardIndex, setExpandedCardIndex] = useState<number | null>(null);
  const [exporting, setExporting] = useState(false);

  // ============================================================
  // SEARCH HISTORY STATE
  // ============================================================
  const [searchHistory, setSearchHistory] = useState<SearchHistoryEntry[]>(loadHistory);
  const lastSavedQueryRef = useRef<string>('');

  // Save history entry when search results arrive
  useEffect(() => {
    if (
      searchResults &&
      !searchResults.tokenLimitReached &&
      searchQuery.trim() &&
      searchQuery !== lastSavedQueryRef.current
    ) {
      lastSavedQueryRef.current = searchQuery;
      const entry: SearchHistoryEntry = {
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        query: searchQuery,
        timestamp: Date.now(),
        resultCount: searchResults.resultCount ?? searchResults.results?.length ?? 0,
      };
      setSearchHistory((prev) => {
        // Remove duplicate queries (keep newest)
        const filtered = prev.filter((h) => h.query !== entry.query);
        const updated = [entry, ...filtered].slice(0, MAX_HISTORY_ENTRIES);
        saveHistory(updated);
        return updated;
      });
    }
  }, [searchResults]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRerunSearch = useCallback((query: string) => {
    handleSearch(query);
  }, [handleSearch]);

  const handleRemoveHistoryEntry = useCallback((id: string) => {
    setSearchHistory((prev) => {
      const updated = prev.filter((h) => h.id !== id);
      saveHistory(updated);
      return updated;
    });
  }, []);

  const handleClearHistory = useCallback(() => {
    setSearchHistory([]);
    saveHistory([]);
  }, []);

  // Determine if results are product-like
  const hasProductResults = useMemo(() => {
    if (!searchResults?.results || searchResults.results.length === 0) return false;
    return isProductResult(searchResults.results[0]);
  }, [searchResults]);

  // Reset expanded card when search results change
  useEffect(() => { setExpandedCardIndex(null); }, [searchResults]);

  // CSV export of search results (A5.4)
  const handleExportCSV = () => {
    if (!searchResults?.results || searchResults.results.length === 0) return;
    setExporting(true);
    try {
      const rows = searchResults.results;

      const escapeCSV = (val: string): string => {
        if (val.includes(',') || val.includes('"') || val.includes('\n')) {
          return `"${val.replace(/"/g, '""')}"`;
        }
        return val;
      };

      let csvHeaders: string[];
      let csvRows: string[][];

      if (hasProductResults) {
        // Product-specific columns per issue #65
        csvHeaders = [
          'Product Name', 'NDC', 'Manufacturer',
          'Risk', 'BAA Eligible', 'Country of Origin',
        ];
        csvRows = rows.map((row: Record<string, unknown>) => [
          String(getField(row, 'product_name', 'proprietary_name', 'name') ?? ''),
          String(getField(row, 'ndc_code', 'ndc') ?? ''),
          String(getField(row, 'manufacturer') ?? ''),
          String(getField(row, 'supply_chain_risk', 'risk', 'risk_level') ?? ''),
          getField(row, 'baa_eligible') ? 'Yes' : 'No',
          String(getField(row, 'country_of_origin', 'api_source_country', 'finished_goods_country') ?? ''),
        ]);
      } else {
        // Generic results — use all column keys
        const keys = Object.keys(rows[0]);
        csvHeaders = keys;
        csvRows = rows.map((row: Record<string, unknown>) =>
          keys.map(k => String(row[k] ?? ''))
        );
      }

      const csvContent = [
        csvHeaders.map(escapeCSV).join(','),
        ...csvRows.map(r => r.map(escapeCSV).join(',')),
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const timestamp = new Date().toISOString().slice(0, 10);
      link.download = `search-results-${timestamp}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <SectionHeader sub="Query your product database using natural language intelligence">AI-Powered Drug Intelligence Search</SectionHeader>

      {/* Search Input */}
      <div style={{ position: 'relative' }}>
        <div style={{ display: 'flex', gap: 8 }}>
          <div style={{ flex: 1, position: 'relative' }}>
            <span style={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', color: T.textLight }}><IconSearch /></span>
            <input
              ref={searchInputRef}
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
              placeholder="Ask anything about your product database..."
              style={{
                width: '100%', padding: '16px 16px 16px 48px', borderRadius: T.radius,
                border: `2px solid ${T.border}`, background: T.white, color: T.textDark,
                fontSize: 15, outline: 'none', boxSizing: 'border-box',
                transition: 'border-color 0.2s', fontFamily: 'inherit',
              }}
            />
          </div>
          <button
            onClick={() => handleSearch()}
            disabled={isSearching || !searchQuery.trim()}
            style={{
              padding: '0 28px', borderRadius: T.radius, border: 'none',
              background: T.navy, color: T.white, fontWeight: 700, fontSize: 14,
              cursor: 'pointer', opacity: isSearching || !searchQuery.trim() ? 0.5 : 1,
              transition: 'opacity 0.2s', fontFamily: 'inherit',
            }}
          >
            {isSearching ? <IconSpinner /> : 'Search'}
          </button>
        </div>

        {/* Suggested Queries */}
        <div style={{ display: 'flex', gap: 6, marginTop: 12, flexWrap: 'wrap' }}>
          {['Show me all BAA-eligible products', 'Which products have API from China?', "What's our biggest supply chain risk?", 'Show products in shortage'].map(q => (
            <button
              key={q}
              onClick={() => { setSearchQuery(q); }}
              style={{
                padding: '6px 14px', borderRadius: 20,
                border: `1px solid ${T.border}`, background: T.white,
                color: T.textMid, fontSize: 12, cursor: 'pointer',
                transition: 'border-color 0.2s', fontFamily: 'inherit',
              }}
            >
              {q}
            </button>
          ))}
        </div>
      </div>

      {/* Search History Panel */}
      <SearchHistoryPanel
        history={searchHistory}
        onRerun={handleRerunSearch}
        onRemove={handleRemoveHistoryEntry}
        onClear={handleClearHistory}
      />

      {/* Loading */}
      {isSearching && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
          <IconSpinner />
        </div>
      )}

      {/* Token Limit */}
      {searchResults?.tokenLimitReached && (
        <div style={{ background: T.dangerBg, border: '1px solid #FECACA', borderRadius: T.radius, padding: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <span style={{ fontSize: 24 }}>&#x26A0;</span>
            <span style={{ fontSize: 14, fontWeight: 700, color: T.danger, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Token Limit Reached</span>
          </div>
          <p style={{ fontSize: 14, color: T.textDark, lineHeight: 1.7, margin: 0 }}>
            {searchResults.tokenLimitMessage}
          </p>
          {onNavigateToAccount && (
            <button
              onClick={onNavigateToAccount}
              style={{
                marginTop: 16, padding: '10px 24px', borderRadius: T.radiusSm,
                border: 'none', background: T.navy, color: T.white,
                fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
              }}
            >
              View Plans &amp; Upgrade
            </button>
          )}
        </div>
      )}

      {/* Results */}
      {searchResults && !searchResults.tokenLimitReached && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* Intelligence Briefing Summary */}
          <div style={{
            background: T.offWhite, border: `1px solid ${T.border}`,
            borderRadius: T.radius,
            padding: 24,
            position: 'relative',
            overflow: 'hidden',
          }}>
            {/* Red accent bar */}
            <div style={{
              position: 'absolute', top: 0, left: 0, right: 0, height: 3,
              background: `linear-gradient(90deg, ${T.red}, ${T.red}80, transparent)`,
            }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <div style={{
                width: 32, height: 32, borderRadius: 8,
                background: `${T.red}20`, display: 'flex',
                alignItems: 'center', justifyContent: 'center',
              }}>
                <span style={{ color: T.red }}><IconBot /></span>
              </div>
              <span style={{
                fontSize: 12, fontWeight: 700, color: T.red,
                textTransform: 'uppercase', letterSpacing: '0.1em',
                fontFamily: "'IBM Plex Mono', 'Courier New', monospace",
              }}>
                Intelligence Briefing
              </span>
            </div>
            <p style={{
              fontSize: 14, color: T.textDark,
              lineHeight: 1.8, whiteSpace: 'pre-wrap', margin: 0,
            }}>
              {searchResults.answer}
            </p>
          </div>

          {/* Metadata Badges */}
          {(searchResults.confidence || searchResults.metadata || searchResults.resultCount !== undefined) && (
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
              {searchResults.confidence && (
                <span style={{
                  padding: '6px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                  background: searchResults.confidence === 'HIGH' ? T.successBg : searchResults.confidence === 'MEDIUM' ? T.warningBg : T.dangerBg,
                  color: searchResults.confidence === 'HIGH' ? T.success : searchResults.confidence === 'MEDIUM' ? T.warning : T.danger,
                  border: `1px solid ${searchResults.confidence === 'HIGH' ? '#BBF7D0' : searchResults.confidence === 'MEDIUM' ? '#FDE68A' : '#FECACA'}`,
                }}>
                  Confidence: {searchResults.confidence}
                </span>
              )}
              {searchResults.resultCount !== undefined && (
                <span style={{
                  padding: '6px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                  background: T.offWhite, color: T.textMid, border: `1px solid ${T.border}`,
                }}>
                  {searchResults.resultCount} {hasProductResults ? 'products' : 'results'}
                </span>
              )}
              {searchResults.metadata?.elapsed_seconds != null && (
                <span style={{
                  padding: '6px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                  background: T.offWhite, color: T.textMid, border: `1px solid ${T.border}`,
                }}>
                  {searchResults.metadata.elapsed_seconds.toFixed(2)}s
                </span>
              )}
              {searchResults.metadata?.cost_usd != null && (
                <span style={{
                  padding: '6px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                  background: T.offWhite, color: T.textMid, border: `1px solid ${T.border}`,
                }}>
                  ${searchResults.metadata.cost_usd.toFixed(4)}
                </span>
              )}
              {searchResults.metadata?.tokens_used != null && (
                <span style={{
                  padding: '6px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                  background: T.offWhite, color: T.textMid, border: `1px solid ${T.border}`,
                }}>
                  {searchResults.metadata.tokens_used} tokens
                </span>
              )}

              {/* SQL Toggle */}
              {searchResults.sql && (
                <button
                  onClick={() => setShowSql(!showSql)}
                  style={{
                    padding: '6px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                    background: showSql ? T.navy : T.offWhite,
                    color: showSql ? T.gold : T.textMid,
                    border: `1px solid ${showSql ? T.navy : T.border}`,
                    cursor: 'pointer', transition: 'all 0.2s', fontFamily: 'inherit',
                  }}
                >
                  {showSql ? 'Hide SQL' : 'Show SQL'}
                </button>
              )}
            </div>
          )}

          {/* SQL Query (collapsible) */}
          {searchResults.sql && showSql && (
            <div style={{ background: T.navy, borderRadius: T.radius, padding: 20, overflow: 'hidden' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: T.gold, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Generated SQL</span>
                <button
                  onClick={() => copyToClipboard(searchResults.sql || '').then(() => alert('SQL copied!'))}
                  style={{
                    padding: '4px 12px', borderRadius: 6, border: 'none',
                    background: 'rgba(255,255,255,0.1)', color: T.white,
                    fontSize: 11, cursor: 'pointer', fontFamily: 'inherit',
                  }}
                >
                  Copy
                </button>
              </div>
              <pre style={{
                margin: 0, fontSize: 12, color: '#E2E8F0',
                fontFamily: "'IBM Plex Mono', monospace",
                whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.6,
              }}>
                {searchResults.sql}
              </pre>
            </div>
          )}

          {/* Result Cards or Table */}
          {searchResults.results && searchResults.results.length > 0 && (
            <>
              {hasProductResults ? (
                <div>
                  {/* Results header */}
                  <div style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    marginBottom: 12,
                  }}>
                    <span style={{
                      fontSize: 11, fontWeight: 700, color: T.textLight,
                      textTransform: 'uppercase', letterSpacing: '0.08em',
                    }}>
                      Product Intelligence Cards ({searchResults.resultCount ?? searchResults.results.length})
                    </span>
                    <button
                      onClick={handleExportCSV}
                      disabled={exporting}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        padding: '6px 16px', borderRadius: 20,
                        border: `1px solid ${T.gold}`, background: `${T.gold}10`,
                        color: T.gold, fontSize: 12, fontWeight: 700,
                        cursor: exporting ? 'not-allowed' : 'pointer',
                        opacity: exporting ? 0.6 : 1,
                        transition: 'all 0.2s', fontFamily: 'inherit',
                        letterSpacing: '0.02em',
                      }}
                    >
                      {exporting ? 'Exporting…' : '⬇ Export CSV'}
                    </button>
                  </div>

                  {/* Cards */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {searchResults.results.slice(0, 25).map((row, i) => (
                      <BriefingCard
                        key={i}
                        row={row}
                        index={i}
                        expanded={expandedCardIndex === i}
                        onToggleExpand={() => setExpandedCardIndex(expandedCardIndex === i ? null : i)}
                        onNavigate={onNavigateToProduct}
                      />
                    ))}
                  </div>

                  {searchResults.results.length > 25 && (
                    <div style={{
                      padding: '16px 20px', textAlign: 'center',
                      color: T.textLight, fontSize: 13, marginTop: 8,
                      background: T.offWhite, borderRadius: T.radiusSm,
                    }}>
                      Showing 25 of {searchResults.results.length} products. Refine your search for more specific results.
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  <div style={{
                    display: 'flex', justifyContent: 'flex-end',
                    marginBottom: 8,
                  }}>
                    <button
                      onClick={handleExportCSV}
                      disabled={exporting}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        padding: '6px 16px', borderRadius: 20,
                        border: `1px solid ${T.gold}`, background: `${T.gold}10`,
                        color: T.gold, fontSize: 12, fontWeight: 700,
                        cursor: exporting ? 'not-allowed' : 'pointer',
                        opacity: exporting ? 0.6 : 1,
                        transition: 'all 0.2s', fontFamily: 'inherit',
                        letterSpacing: '0.02em',
                      }}
                    >
                      {exporting ? 'Exporting…' : '⬇ Export CSV'}
                    </button>
                  </div>
                  <ResultsTable
                    results={searchResults.results}
                    resultCount={searchResults.resultCount}
                  />
                </div>
              )}
            </>
          )}

          {/* Insights */}
          {searchResults.insights?.length > 0 && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {searchResults.insights.map((ins, i) => (
                <div key={i} style={{
                  flex: '1 1 250px', padding: '12px 16px',
                  background: T.white, border: `1px solid ${T.border}`,
                  borderRadius: T.radiusSm, boxShadow: T.shadow,
                  display: 'flex', alignItems: 'flex-start', gap: 8,
                }}>
                  <span style={{ color: T.gold, flexShrink: 0 }}><IconStar /></span>
                  <span style={{ fontSize: 12, color: T.textMid, lineHeight: 1.5 }}>{ins}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
